import { useState, useMemo, useEffect, type ComponentProps } from 'react'
import { useParams, useNavigate } from 'react-router'
import { ArrowLeft, ChevronDown, ChevronUp, Info, Copy, Check, Star } from 'lucide-react'
import { useReservation, useReservationThread } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { ToastAction } from '@/components/ui/toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { MessageBubble } from '@/components/reservation/message-bubble'
import { InstructionsModal } from '@/components/reservation/instructions-modal'
import { SecurityDepositCard } from '@/components/reservation/security-deposit-card'
import { ReservationExternalLinks } from '@/components/reservation/external-links'
import { ConfidenceCard } from '@/components/reservation/confidence-card'
import { ChannelMark } from '@/components/channel-icons'
import { type Suggestion } from '@/components/conversation/suggested-reply-card'
import {
  formatTime,
  formatDate,
  formatISODateTime,
  formatDateRange,
  formatCurrency,
  getChannelLabel,
  getChannelBadgeVariant,
  getStatusBadgeVariant,
  calculateNights,
} from '@/lib/format-utils'

/**
 * Shape of the Uplisting-sourced reservation data this page reads - only the
 * fields it actually consumes. `GetReservationResponse['external']` is typed
 * `unknown` by @natalan/capehost-contracts (the reservation KV record is a
 * long-lived blob written by several independent pipelines, deliberately
 * not modeled as a closed shape - see reservations.d.ts's own doc comment),
 * so this mirrors the same local-interface pattern already used for this
 * exact data by src/components/messages/reservation-sidebar.tsx and
 * src/lib/format-utils.ts, just with the fuller field set this page reads.
 */
interface UplistingData {
  preferred_guest_name?: string
  guest_name?: string
  property_name?: string
  channel?: string
  check_in?: string
  check_out?: string
  number_of_nights?: number
  number_of_guests?: number
  status?: string
  note?: string | null
  lock_code?: string | null
  arrival_time?: string
  departure_time?: string
  booked_at?: string
  external_reservation_id?: string
  accomodation_total?: number
  cleaning_fee?: number
  gross_revenue?: number
  commission?: number
  net_revenue?: number
  total_payout?: number
  balance?: number
}

export function ReservationDetailsPage() {
  const { reservationId } = useParams<{ reservationId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const {
    data: reservationData,
    isLoading: isLoadingReservation,
    error: reservationError,
    refetch: refetchReservation,
  } = useReservation(reservationId)
  const reservation = reservationData
  const {
    data: threadData,
    isLoading: isLoadingThread,
    error: threadError,
    refetch: refetchThread,
  } = useReservationThread(reservationId)

  // Local state for streaming suggestions (merged with embedded message suggestions)
  const [localStreamingSuggestions, setLocalStreamingSuggestions] = useState<
    Record<string, Suggestion[]>
  >({})
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null)

  // Extract suggestions from embedded message data and merge with local streaming state
  const suggestionsByMessageId = useMemo<Record<string, Suggestion[]>>(() => {
    // Extract suggestions from messages (embedded in reservation thread response).
    // Cast: ReservationThreadMessage['suggestions'] (contract-typed, `error`/
    // `metadata` shaped for the backend's own record) is deliberately assigned
    // here to the looser `Suggestion` shape MessageBubble/SuggestedReplyCard
    // already expect (components/conversation/suggested-reply-card.tsx) -
    // display-side code only ever reads these defensively, and this performs
    // no runtime transformation of the underlying array.
    const embeddedSuggestions: Record<string, Suggestion[]> = {}
    if (threadData?.messages) {
      threadData.messages.forEach(message => {
        if (message.suggestions && Array.isArray(message.suggestions)) {
          embeddedSuggestions[message.id] = message.suggestions as unknown as Suggestion[]
        }
      })
    }

    // Merge with local streaming suggestions, intelligently merging by ID
    // If API has a more final status (completed/failed), prefer API over local streaming state
    const merged: Record<string, Suggestion[]> = { ...embeddedSuggestions }
    Object.keys(localStreamingSuggestions).forEach(messageId => {
      if (merged[messageId]) {
        const local = localStreamingSuggestions[messageId]
        const api = merged[messageId]

        // Create a map of API suggestions by ID for quick lookup
        const apiById = new Map(api.map(s => [s.id, s]))

        // Merge: prefer API if it has final status (completed/failed), otherwise keep local for streaming states
        const mergedSuggestions = local.map(localSuggestion => {
          const apiSuggestion = apiById.get(localSuggestion.id)
          if (apiSuggestion) {
            const apiStatus = (apiSuggestion.status || '').toLowerCase()
            // If API has final status, prefer it; otherwise keep local (for streaming/queued states)
            if (apiStatus === 'completed' || apiStatus === 'failed' || apiStatus === 'accepted') {
              return apiSuggestion
            }
            // Keep local for streaming/queued states, but merge text if API has more
            return {
              ...localSuggestion,
              text: apiSuggestion.text || localSuggestion.text,
              status: apiSuggestion.status || localSuggestion.status,
            }
          }
          return localSuggestion
        })

        // Add API suggestions that aren't in local
        const localIds = new Set(local.map(s => s.id))
        const apiOnly = api.filter(s => !localIds.has(s.id))
        merged[messageId] = [...mergedSuggestions, ...apiOnly]
      } else {
        merged[messageId] = localStreamingSuggestions[messageId]
      }
    })
    return merged
  }, [threadData, localStreamingSuggestions])

  // Clean up local streaming state when API returns final statuses (completed/failed)
  useEffect(() => {
    if (!threadData?.messages) return

    const toRemove: Array<{ messageId: string; suggestionId: string | undefined }> = []
    const messagesToClearStreaming = new Set<string>()
    Object.keys(localStreamingSuggestions).forEach(messageId => {
      const local = localStreamingSuggestions[messageId]
      const apiSuggestions = threadData.messages.find(m => m.id === messageId)?.suggestions || []

      local.forEach(localSuggestion => {
        const apiSuggestion = apiSuggestions.find(s => s.id === localSuggestion.id)
        if (apiSuggestion) {
          const apiStatus = (apiSuggestion.status || '').toLowerCase()
          if (apiStatus === 'completed' || apiStatus === 'failed' || apiStatus === 'accepted') {
            toRemove.push({ messageId, suggestionId: localSuggestion.id })
            // Also clear streamingMessageId if this message was streaming
            messagesToClearStreaming.add(messageId)
          }
        }
      })
    })

    // Also check merged suggestions to catch cases where local state was already cleaned up
    // but streamingMessageId is still set
    if (streamingMessageId) {
      const suggestions = suggestionsByMessageId[streamingMessageId] || []
      const hasFinalStatus = suggestions.some(s => {
        const status = (s.status || '').toLowerCase()
        return status === 'completed' || status === 'failed' || status === 'accepted'
      })
      if (hasFinalStatus) {
        messagesToClearStreaming.add(streamingMessageId)
      }
    }

    if (toRemove.length > 0) {
      // Syncing local streaming-suggestion overlay state to newly-arrived API
      // data (removing entries once the server confirms a final status), not
      // deriving state from props available at render time -- there's no
      // render-time equivalent since this clears accumulated local state.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalStreamingSuggestions(prev => {
        const updated = { ...prev }
        toRemove.forEach(({ messageId, suggestionId }) => {
          if (updated[messageId]) {
            updated[messageId] = updated[messageId].filter(s => s.id !== suggestionId)
            if (updated[messageId].length === 0) {
              delete updated[messageId]
            }
          }
        })
        return updated
      })
    }

    // Clear streamingMessageId for messages that have reached final status
    if (
      messagesToClearStreaming.size > 0 &&
      streamingMessageId &&
      messagesToClearStreaming.has(streamingMessageId)
    ) {
      setStreamingMessageId(null)
    }
  }, [threadData?.messages, localStreamingSuggestions, streamingMessageId, suggestionsByMessageId])

  // Check if any suggestions are pending to enable polling
  // Use merged suggestionsByMessageId to include both API and local streaming state
  const hasPendingSuggestions = useMemo(() => {
    const pending = Object.values(suggestionsByMessageId).some(suggestions => {
      return suggestions.some(s => {
        const status = (s.status || '').toLowerCase()
        const hasText = s.text && s.text.trim().length > 0
        // Treat in-progress status as pending, but skip "queued" if it has text (likely completed but status not updated)
        if (status === 'queued' && hasText) {
          return false // Don't treat as pending if it has text
        }
        return status === 'queued' || status === 'generating' || status === 'streaming'
      })
    })
    return pending
  }, [suggestionsByMessageId])

  // Poll every 2 seconds if there are pending suggestions
  useEffect(() => {
    if (!hasPendingSuggestions) return

    const interval = setInterval(() => {
      refetchThread()
    }, 2000)

    return () => clearInterval(interval)
  }, [hasPendingSuggestions, refetchThread])
  // Instructions state: Record<messageId, string>
  const [instructionsByMessageId, setInstructionsByMessageId] = useState<Record<string, string>>({})
  // Current message ID for instructions modal
  const [instructionsModalMessageId, setInstructionsModalMessageId] = useState<string | null>(null)
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false)

  // Handle generate/regenerate for a specific message (with SSE streaming)
  const handleRegenerate = async (
    messageId: string,
    mode: string = 'regenerate',
    options: { instruction?: string; imageUrls?: string[] } = {}
  ) => {
    if (!threadId) {
      console.error('Cannot generate suggestion: thread ID not available')
      return
    }

    const instructions = options.instruction || instructionsByMessageId[messageId] || ''
    const imageUrls = options.imageUrls || []
    setStreamingMessageId(messageId)

    // Initialize or get existing suggestions for this message
    const currentSuggestions = suggestionsByMessageId[messageId] || []

    // Update existing completed suggestions to "queued" status for immediate UI feedback
    // This shows the user that regeneration is in progress before the API responds
    const updatedExistingSuggestions = currentSuggestions
      .filter(s => s.status === 'completed' || s.status === 'accepted')
      .map(s => ({
        ...s,
        status: 'queued', // Show as generating/queued
        updatedAt: new Date().toISOString(),
      }))

    // Add updated existing suggestions to local streaming state for immediate UI update
    if (updatedExistingSuggestions.length > 0) {
      setLocalStreamingSuggestions(prev => ({
        ...prev,
        [messageId]: [
          ...updatedExistingSuggestions,
          ...(prev[messageId] || []).filter(
            s => !updatedExistingSuggestions.some(updated => updated.id === s.id)
          ),
        ],
      }))
    }

    const latestVersion =
      currentSuggestions.length > 0 ? Math.max(...currentSuggestions.map(s => s.version || 0)) : 0
    const nextVersion = latestVersion + 1

    // Create a temporary suggestion object that will be updated as we stream.
    // Deliberately not annotated as `Suggestion` (that type doesn't declare
    // messageId/threadId/reservationId) so these extra debug-only fields -
    // never read by this page, but shown verbatim by SuggestedReplyCard's
    // "Full Suggestion Object" JSON dump - are preserved unchanged; TS still
    // structurally accepts this object wherever a `Suggestion` is expected
    // below, since every field that overlaps by name is compatible.
    const tempSuggestion = {
      // handleRegenerate only ever runs from user-triggered event handlers
      // (verified call sites), never during render, so Date.now() here is safe.
      // eslint-disable-next-line react-hooks/purity
      id: `temp-${messageId}-${Date.now()}`,
      messageId,
      threadId,
      reservationId: reservationId || null,
      createdAt: new Date().toISOString(),
      version: nextVersion,
      instructions: instructions || undefined,
      text: '',
      status: 'streaming', // Set initial status so polling can detect it immediately
    }

    // Add temporary suggestion to local state for streaming
    setLocalStreamingSuggestions(prev => ({
      ...prev,
      [messageId]: [...(prev[messageId] || []), tempSuggestion],
    }))

    try {
      let fullText = ''
      let suggestionId = tempSuggestion.id

      await api.regenerateSuggestion(
        messageId,
        { instruction: instructions, mode, imageUrls },
        (eventType, data) => {
          if (eventType === 'meta') {
            suggestionId = data.suggestionId || suggestionId
            // Update suggestion ID in local state
            setLocalStreamingSuggestions(prev => {
              const suggestions = prev[messageId] || []
              const updated = suggestions.map(s =>
                s.id === tempSuggestion.id
                  ? { ...s, id: suggestionId, version: data.version || nextVersion }
                  : s
              )
              return { ...prev, [messageId]: updated }
            })
            // `api.regenerateSuggestion`'s onEvent type only ever fires
            // 'meta' | 'done' | 'error' (src/api/client.ts) - it never
            // actually emits 'delta' today, so this branch is dead code,
            // preserved as-is by this type-only conversion (cast needed
            // purely so the literal comparison still type-checks).
          } else if ((eventType as string) === 'delta') {
            fullText += data.text || ''
            // Update text in real-time
            setLocalStreamingSuggestions(prev => {
              const suggestions = prev[messageId] || []
              const updated = suggestions.map(s =>
                s.id === suggestionId ? { ...s, text: fullText } : s
              )
              return { ...prev, [messageId]: updated }
            })
          } else if (eventType === 'done') {
            // Final update with complete text and status
            fullText = data.text || fullText
            const status = data.status || 'streaming'
            setLocalStreamingSuggestions(prev => {
              const suggestions = prev[messageId] || []
              const updated = suggestions.map(s =>
                s.id === suggestionId ? { ...s, text: fullText, status } : s
              )
              return { ...prev, [messageId]: updated }
            })
            // If status is completed, clear streaming state and refetch
            // Otherwise keep streaming state so polling can detect pending status
            if (status === 'completed' || status === 'failed') {
              setTimeout(() => {
                refetchSuggestions()
                refetchThread()
                setStreamingMessageId(null)
              }, 500)
            } else {
              // Status is queued/streaming/generating - keep streaming state active
              // Polling will detect this and continue refetching until completed
              refetchThread()
            }
          } else if (eventType === 'error') {
            console.error('Suggestion generation error:', data)
            setLocalStreamingSuggestions(prev => {
              const suggestions = prev[messageId] || []
              // Remove the temporary suggestion on error
              const updated = suggestions.filter(
                s => s.id !== tempSuggestion.id && s.id !== suggestionId
              )
              return { ...prev, [messageId]: updated }
            })
            setStreamingMessageId(null)
            toast({
              title: 'Suggestion generation failed',
              description: data.message || 'Failed to generate suggestion',
              variant: 'destructive',
              // data.unreachable means the API session likely expired (see
              // ApiUnreachableError in src/api/client.js) — let the user
              // re-authenticate without losing the toast to a plain log line.
              action: data.unreachable ? (
                <ToastAction
                  altText="Re-authenticate"
                  onClick={() => window.open(data.baseUrl, '_blank', 'noopener,noreferrer')}
                >
                  Re-authenticate
                </ToastAction>
              ) : undefined,
            })
          }
        }
      )
    } catch (error: unknown) {
      console.error('Error generating suggestion:', error)
      // Remove temporary suggestion on error
      setLocalStreamingSuggestions(prev => {
        const suggestions = prev[messageId] || []
        const updated = suggestions.filter(s => s.id !== tempSuggestion.id)
        return { ...prev, [messageId]: updated }
      })
      setStreamingMessageId(null)
      toast({
        title: 'Suggestion generation failed',
        description: error instanceof Error ? error.message : 'Failed to generate suggestion',
        variant: 'destructive',
      })
    }
  }

  // Handle add instructions for a specific message
  const handleAddInstructions = (messageId: string) => {
    setInstructionsModalMessageId(messageId)
    setInstructionsModalOpen(true)
  }

  // Handle rewrite (save instructions and regenerate)
  const handleRewrite = async (messageId: string, instructions: string, imageUrls: string[]) => {
    // Save instructions locally
    setInstructionsByMessageId(prev => ({
      ...prev,
      [messageId]: instructions,
    }))

    // Regenerate suggestion with instructions and images
    await handleRegenerate(messageId, 'regenerate', {
      instruction: instructions,
      imageUrls,
    })
  }

  // Handle cancel instructions
  const handleCancelInstructions = () => {
    setInstructionsModalOpen(false)
    setInstructionsModalMessageId(null)
  }

  // Handle reprocess message
  const handleReprocess = async (messageId: string) => {
    setReprocessingMessageId(messageId)
    try {
      await api.reprocessMessage(messageId)
      toast({
        title: 'Message reprocessed',
        description: 'Message has been reprocessed from original email data',
      })
      // Refetch thread to get updated message
      refetchThread()
    } catch (error: unknown) {
      toast({
        title: 'Reprocess failed',
        description: error instanceof Error ? error.message : 'Failed to reprocess message',
        variant: 'destructive',
      })
    } finally {
      setReprocessingMessageId(null)
    }
  }

  // Handle copy field (reservation ID or external ID)
  const handleCopyField = async (fieldType: 'reservationId' | 'externalId', text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(fieldType)
      setTimeout(() => setCopiedField(null), 3000)
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  // Handle copy message
  const handleCopyMessage = async (messageId: string, messageBody: string) => {
    try {
      await navigator.clipboard.writeText(messageBody)
      setCopiedMessageId(messageId)
      setTimeout(() => setCopiedMessageId(null), 3000)
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy message to clipboard',
        variant: 'destructive',
      })
    }
  }

  // Handle re-assign message
  const handleReassignMessage = async (
    messageId: string,
    propertyId: string,
    reservationId: string | null = null
  ) => {
    setReassigningMessageId(messageId)
    try {
      await api.reassignMessage(messageId, {
        property_id: propertyId,
        reservation_id: reservationId,
      })

      // Clear suggestions for this message
      setLocalStreamingSuggestions(prev => {
        const updated = { ...prev }
        delete updated[messageId]
        return updated
      })

      // Clear instructions for this message
      setInstructionsByMessageId(prev => {
        const updated = { ...prev }
        delete updated[messageId]
        return updated
      })

      // Show toast with link to new reservation if applicable
      if (reservationId) {
        toast({
          title: 'Message re-assigned',
          description: 'Message has been re-assigned to the selected reservation',
          action: (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/reservations/${reservationId}`)}
            >
              View reservation
            </Button>
          ),
        })
      } else {
        toast({
          title: 'Message re-assigned',
          description: 'Message has been re-assigned to the property',
        })
      }

      // Refetch thread to get updated message
      const threadResult = await refetchThread()
      // Also refetch reservation in case we're viewing a different reservation now
      await refetchReservation()

      // Regenerate suggestion for the reassigned message (if it's a guest message)
      try {
        // Check if message is still a guest message after reassignment
        const updatedMessages = threadResult?.data?.messages || []
        const updatedMessage = updatedMessages.find(m => m.id === messageId)
        if (updatedMessage) {
          // Cast: `direction` is normalized to 3 values by the contract
          // (guest_to_host/host_to_guest/system_to_host), but this check
          // also defends against raw/legacy values ('guest', 'inbound') the
          // same way src/components/reservation/message-bubble.tsx's own
          // (locally string-typed) isGuest check does - preserved as-is.
          const direction = updatedMessage.direction as string
          const isGuest =
            direction === 'guest' ||
            direction === 'guest_to_host' ||
            direction === 'inbound' ||
            !direction
          if (isGuest) {
            // Regenerate with empty text to clear existing suggestions
            await handleRegenerate(messageId, 'generate')
          }
        }
      } catch (error: unknown) {
        // Silently fail - regeneration is not critical
        console.error('Failed to regenerate suggestion after reassignment:', error)
      }
    } catch (error: unknown) {
      toast({
        title: 'Re-assignment failed',
        description: error instanceof Error ? error.message : 'Failed to re-assign message',
        variant: 'destructive',
      })
    } finally {
      setReassigningMessageId(null)
    }
  }

  const isLoading = isLoadingReservation || isLoadingThread
  const error = reservationError || threadError

  // Extract reservation data
  const uplistingData = useMemo<UplistingData>(
    () => (reservation?.external as { uplisting?: UplistingData } | undefined)?.uplisting || {},
    [reservation?.external]
  )
  const messages = threadData?.messages || []
  const threadCount = threadData ? 1 : 0
  const threadId = threadData?.thread?.threadId || null

  const [reprocessingMessageId, setReprocessingMessageId] = useState<string | null>(null)
  const [reassigningMessageId, setReassigningMessageId] = useState<string | null>(null)
  // State for tracking copied fields (reservation ID or external ID)
  const [copiedField, setCopiedField] = useState<'reservationId' | 'externalId' | null>(null)
  // State for tracking copied messages
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  // Historic guest messages the operator manually expanded to view/generate a suggested reply.
  // Not persisted - collapses again on next visit so old suggestions don't clutter the feed.
  const [expandedSuggestionMessageIds, setExpandedSuggestionMessageIds] = useState<Set<string>>(
    () => new Set()
  )

  const toggleSuggestionExpanded = (messageId: string) => {
    setExpandedSuggestionMessageIds(prev => {
      const next = new Set(prev)
      if (next.has(messageId)) {
        next.delete(messageId)
      } else {
        next.add(messageId)
      }
      return next
    })
  }

  // Only the thread's last message can have a live suggested-reply bubble, and only when
  // that last message is itself an unanswered guest message - mirrors the backend's
  // handleReservationThread filtering (admin-api reservations.js) so the feed doesn't show
  // a "Not generated" placeholder under every historical guest message.
  const lastUnansweredGuestMessageId = useMemo(() => {
    const threadMessages = threadData?.messages || []
    if (threadMessages.length === 0) return null
    const lastMessage = threadMessages[threadMessages.length - 1]
    // Cast: see the identical comment on the reassign-flow direction check above.
    const direction = lastMessage.direction as string
    const isGuest =
      direction === 'guest' ||
      direction === 'guest_to_host' ||
      direction === 'inbound' ||
      !direction
    return isGuest ? lastMessage.id : null
  }, [threadData?.messages])

  // Refetch function for suggestions (refetches the thread to get updated suggestions)
  const refetchSuggestions = () => {
    refetchThread()
  }

  // Guest display name
  const guestDisplayName = useMemo(() => {
    return (
      uplistingData.preferred_guest_name ||
      reservation?.guest_name ||
      uplistingData.guest_name ||
      'Guest'
    )
  }, [reservation?.guest_name, uplistingData])

  // Property name
  const propertyName = useMemo(() => {
    return uplistingData.property_name || 'Property'
  }, [uplistingData])

  // Channel
  const channel = useMemo(() => {
    return reservation?.channel || uplistingData.channel || null
  }, [reservation?.channel, uplistingData])

  // Dates
  const checkIn = reservation?.check_in || uplistingData.check_in
  const checkOut = reservation?.check_out || uplistingData.check_out

  // Nights
  const nights = useMemo(() => {
    return uplistingData.number_of_nights || calculateNights(checkIn ?? '', checkOut ?? '')
  }, [uplistingData, checkIn, checkOut])

  // Guests
  const guests = uplistingData.number_of_guests || null

  // Status
  const status = useMemo(() => {
    return uplistingData.status || reservation?.status || null
  }, [reservation?.status, uplistingData])

  // Notes data
  const note = uplistingData.note
  const lockCode = uplistingData.lock_code
  const hasNote = note && note.trim().length > 0

  // Notes expanded state - default to true if note exists
  const [notesExpanded, setNotesExpanded] = useState(false)

  // Set notes to expanded by default when note exists
  useEffect(() => {
    // uplistingData (and hasNote with it) arrives asynchronously after mount;
    // this sets the expanded default the first time a note becomes available,
    // while still letting the user manually collapse it afterward independent
    // of further hasNote changes -- not derivable from render-time data alone.
    if (hasNote) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNotesExpanded(true)
    }
  }, [hasNote])

  // Check-in/out times
  const checkInTime = uplistingData.arrival_time
  const checkOutTime = uplistingData.departure_time

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <LoadingSkeleton className="h-10 w-10" />
          <div className="space-y-2">
            <LoadingSkeleton className="h-8 w-64" />
            <LoadingSkeleton className="h-4 w-96" />
          </div>
        </div>
        <LoadingSkeleton className="h-96 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Reservation {reservationId}</h1>
        </div>
        <ErrorState
          error={error}
          onRetry={() => {
            refetchReservation()
            refetchThread()
          }}
        />
      </div>
    )
  }

  if (!reservation) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Reservation {reservationId}</h1>
        </div>
        <EmptyState message="Reservation not found" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl md:text-3xl font-bold truncate">
              Reservation: {guestDisplayName}
            </h1>
            {/* Meta chips row */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {propertyName && propertyName !== 'Property' && (
                <Badge variant="secondary" className="text-xs">
                  {propertyName}
                </Badge>
              )}
              {channel && (
                <Badge
                  variant={getChannelBadgeVariant(channel) as BadgeProps['variant']}
                  className="text-xs gap-1"
                >
                  <ChannelMark channel={channel} className="h-3.5 w-3.5" />
                  {getChannelLabel(channel)}
                </Badge>
              )}
              {checkIn && checkOut && (
                <Badge variant="secondary" className="text-xs">
                  {formatDateRange(checkIn, checkOut)}
                </Badge>
              )}
              {nights && (
                <Badge variant="secondary" className="text-xs">
                  {nights} night{nights !== 1 ? 's' : ''}
                </Badge>
              )}
              {guests && (
                <Badge variant="secondary" className="text-xs">
                  {guests} guest{guests !== 1 ? 's' : ''}
                </Badge>
              )}
              {status && (
                <Badge
                  variant={getStatusBadgeVariant(status) as BadgeProps['variant']}
                  className="text-xs"
                >
                  {status.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-6">
        {/* Left Column - Details */}
        <div className="space-y-6">
          {/* Notes Card - Callout style, always first if note exists */}
          {hasNote && (
            <Card className="border-l-4 border-l-mint/50 bg-surface2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-mint/70" />
                    <CardTitle>Notes</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    className="h-8 w-8 p-0"
                  >
                    {notesExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              {notesExpanded && (
                <CardContent className="space-y-3 pt-0">
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{note}</div>
                  {lockCode && (
                    <div className="pt-3 border-t border-border">
                      <div className="text-xs text-textMuted mb-1">Lock code</div>
                      <div className="font-mono text-sm">{lockCode}</div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Reservation Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Reservation details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-textMuted mb-1">Check-in</div>
                  <div className="font-medium">
                    {checkInTime
                      ? `${formatDate(checkIn ?? '')} ${formatTime(checkInTime)}`
                      : formatDate(checkIn ?? '')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-textMuted mb-1">Check-out</div>
                  <div className="font-medium">
                    {checkOutTime
                      ? `${formatDate(checkOut ?? '')} ${formatTime(checkOutTime)}`
                      : formatDate(checkOut ?? '')}
                  </div>
                </div>
                {uplistingData.booked_at && (
                  <div>
                    <div className="text-xs text-textMuted mb-1">Booked at</div>
                    <div className="font-medium">{formatISODateTime(uplistingData.booked_at)}</div>
                  </div>
                )}
                {reservationId && (
                  <div>
                    <div className="text-xs text-textMuted mb-1">Reservation ID</div>
                    <div className="flex items-center gap-2">
                      <div className="font-mono text-sm">{reservationId}</div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleCopyField('reservationId', reservationId ?? '')}
                            className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                          >
                            {copiedField === 'reservationId' ? (
                              <Check className="h-4 w-4 text-mint" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {copiedField === 'reservationId' ? 'Copied!' : 'Copy reservation ID'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                )}
                {uplistingData.external_reservation_id && (
                  <div>
                    <div className="text-xs text-textMuted mb-1">External ID</div>
                    <div className="flex items-center gap-2">
                      <div className="font-mono text-sm">
                        {uplistingData.external_reservation_id}
                      </div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() =>
                              handleCopyField(
                                'externalId',
                                uplistingData.external_reservation_id ?? ''
                              )
                            }
                            className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                          >
                            {copiedField === 'externalId' ? (
                              <Check className="h-4 w-4 text-mint" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{copiedField === 'externalId' ? 'Copied!' : 'Copy external ID'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
              <ReservationExternalLinks
                className="mt-4"
                reservationId={reservationId}
                checkIn={checkIn}
                channel={channel}
                externalReservationId={uplistingData.external_reservation_id}
              />
            </CardContent>
          </Card>

          {/* Five-star confidence Card (CAP-301) */}
          <ConfidenceCard reservationId={reservationId ?? ''} />

          {/* Review Card */}
          <Card>
            <CardHeader>
              <CardTitle>Review</CardTitle>
            </CardHeader>
            <CardContent>
              {reservation.review ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={
                        getChannelBadgeVariant(reservation.review.platform) as BadgeProps['variant']
                      }
                      className="text-xs gap-1"
                    >
                      <ChannelMark channel={reservation.review.platform} className="h-3.5 w-3.5" />
                      {getChannelLabel(reservation.review.platform)}
                    </Badge>
                    {reservation.review.rating != null && (
                      <span className="inline-flex items-center gap-1 text-sm font-medium">
                        <Star className="h-3.5 w-3.5 fill-current" />
                        {reservation.review.rating}/5
                      </span>
                    )}
                    {reservation.review.received_at && (
                      <span className="text-xs text-textMuted">
                        {formatDate(reservation.review.received_at)}
                      </span>
                    )}
                  </div>
                  {reservation.review.text && (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                      {reservation.review.text}
                    </p>
                  )}
                  {reservation.review.private_note && (
                    <div className="pt-3 border-t border-border">
                      <div className="text-xs text-textMuted mb-1">Private note to host</div>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">
                        {reservation.review.private_note}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-textMuted">No review yet</p>
              )}
            </CardContent>
          </Card>

          {/* Financials Card */}
          {(uplistingData.accomodation_total !== undefined ||
            uplistingData.cleaning_fee !== undefined ||
            uplistingData.gross_revenue !== undefined ||
            uplistingData.commission !== undefined ||
            uplistingData.net_revenue !== undefined ||
            uplistingData.total_payout !== undefined ||
            uplistingData.balance !== undefined) && (
            <Card className="w-full md:max-w-[65%]">
              <CardHeader className="pb-3">
                <CardTitle>Financials</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {uplistingData.accomodation_total !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-textMuted">Accommodation</span>
                      <span className="font-medium text-sm">
                        {formatCurrency(uplistingData.accomodation_total)}
                      </span>
                    </div>
                  )}
                  {uplistingData.cleaning_fee !== undefined && uplistingData.cleaning_fee !== 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-textMuted">Cleaning fee</span>
                      <span className="font-medium text-sm">
                        {formatCurrency(uplistingData.cleaning_fee)}
                      </span>
                    </div>
                  )}
                  {uplistingData.gross_revenue !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-textMuted">Gross revenue</span>
                      <span className="font-medium text-sm">
                        {formatCurrency(uplistingData.gross_revenue)}
                      </span>
                    </div>
                  )}
                  {uplistingData.commission !== undefined && uplistingData.commission !== 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-textMuted">Commission</span>
                      <span className="font-medium text-xs text-textMuted">
                        {formatCurrency(uplistingData.commission)}
                      </span>
                    </div>
                  )}
                  {uplistingData.net_revenue !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-textMuted">Net revenue</span>
                      <span className="font-medium text-sm">
                        {formatCurrency(uplistingData.net_revenue)}
                      </span>
                    </div>
                  )}
                  {uplistingData.total_payout !== undefined && (
                    <div className="flex justify-between items-center pt-2 border-t border-border mt-2">
                      <span className="text-sm font-medium">Total payout</span>
                      <span className="font-bold text-base">
                        {formatCurrency(uplistingData.total_payout)}
                      </span>
                    </div>
                  )}
                  {uplistingData.balance !== undefined && uplistingData.balance !== 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-textMuted">Balance</span>
                      <span className="font-medium text-xs text-textMuted">
                        {formatCurrency(uplistingData.balance)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Renders nothing unless this reservation actually has a deposit
              record, so it stays invisible on Airbnb stays and on properties
              that don't have the feature turned on. */}
          <SecurityDepositCard reservationId={reservationId ?? ''} />
        </div>

        {/* Right Column - Conversation Panel */}
        <div className="hidden lg:block">
          <div className="sticky top-6">
            {/* Conversation Panel */}
            <Card className="flex flex-col h-[calc(100vh-8rem)] border-2 border-border bg-surface2 shadow-lg">
              <CardHeader className="border-b border-border shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>Conversation</CardTitle>
                    <p className="text-xs text-textMuted mt-1">
                      {threadCount} thread{threadCount !== 1 ? 's' : ''} for this reservation
                    </p>
                  </div>
                </div>
              </CardHeader>

              {/* Messages Area - Scrollable */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                {messages.length === 0 ? (
                  <EmptyState message="No messages yet" />
                ) : (
                  messages.map(message => {
                    // Get suggestions for this message - use embedded suggestions from message or merged state
                    const suggestions = suggestionsByMessageId[message.id] || []
                    const instructions = instructionsByMessageId[message.id] || ''

                    return (
                      <MessageBubble
                        key={message.id}
                        message={
                          message as unknown as ComponentProps<typeof MessageBubble>['message']
                        }
                        guestDisplayName={guestDisplayName}
                        suggestions={suggestions}
                        instructions={instructions}
                        onRegenerate={handleRegenerate}
                        onAddInstructions={handleAddInstructions}
                        onReprocess={handleReprocess}
                        onReassign={handleReassignMessage}
                        reprocessingMessageId={reprocessingMessageId}
                        reassigningMessageId={reassigningMessageId}
                        streamingMessageId={streamingMessageId}
                        copiedMessageId={copiedMessageId}
                        onCopyMessage={handleCopyMessage}
                        isLastUnansweredGuestMessage={message.id === lastUnansweredGuestMessageId}
                        isSuggestionExpanded={expandedSuggestionMessageIds.has(message.id)}
                        onToggleSuggestion={toggleSuggestionExpanded}
                        onFeedbackUpdate={refetchSuggestions}
                      />
                    )
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Mobile Conversation Panel */}
      <div className="lg:hidden">
        <Card className="border-2 border-border bg-surface2 shadow-lg">
          <CardHeader className="border-b border-border">
            <div>
              <CardTitle>Conversation</CardTitle>
              <p className="text-xs text-textMuted mt-1">
                {threadCount} thread{threadCount !== 1 ? 's' : ''} for this reservation
              </p>
            </div>
          </CardHeader>

          {/* Messages Area - Scrollable */}
          <CardContent className="max-h-[500px] overflow-y-auto p-4 space-y-3">
            {messages.length === 0 ? (
              <EmptyState message="No messages yet" />
            ) : (
              messages.map(message => {
                // Get suggestions for this message - use embedded suggestions from message or merged state
                const suggestions = suggestionsByMessageId[message.id] || []
                const instructions = instructionsByMessageId[message.id] || ''

                return (
                  <MessageBubble
                    key={message.id}
                    message={message as unknown as ComponentProps<typeof MessageBubble>['message']}
                    guestDisplayName={guestDisplayName}
                    suggestions={suggestions}
                    instructions={instructions}
                    onRegenerate={handleRegenerate}
                    onAddInstructions={handleAddInstructions}
                    onReprocess={handleReprocess}
                    onReassign={handleReassignMessage}
                    reprocessingMessageId={reprocessingMessageId}
                    reassigningMessageId={reassigningMessageId}
                    streamingMessageId={streamingMessageId}
                    copiedMessageId={copiedMessageId}
                    onCopyMessage={handleCopyMessage}
                    isLastUnansweredGuestMessage={message.id === lastUnansweredGuestMessageId}
                    isSuggestionExpanded={expandedSuggestionMessageIds.has(message.id)}
                    onToggleSuggestion={toggleSuggestionExpanded}
                    onFeedbackUpdate={refetchSuggestions}
                  />
                )
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions Modal */}
      <InstructionsModal
        open={instructionsModalOpen}
        messageId={instructionsModalMessageId}
        initialInstructions={
          (instructionsModalMessageId ? instructionsByMessageId[instructionsModalMessageId] : '') ||
          ''
        }
        onClose={handleCancelInstructions}
        onRewrite={handleRewrite}
      />
    </div>
  )
}
